import { observer } from "owa-mobx-react";
import React from "react";
import loc from "owa-localize";
import { Menu, MenuTrigger, MenuItem, MenuPopover, MenuList, MenuItemRadio, } from "@fluentui/react-components";
import { NumberSymbolRegular, NumberSymbolFilled, bundleIcon } from "@fluentui/react-icons";
import { lazySetFolderCountDisplayMode } from "owa-mail-folder-store";
import { getFolderCountDisplayMode } from "owa-mail-folder-store/lib/selectors/getFolderCountDisplayMode";
import type { FolderCountDisplayMode } from "owa-mail-folder-store/lib/store/schema/MailFolderNodeViewState";
import { getDefaultFolderCountDisplayMode } from "owa-folders";
import { isOffline } from "owa-offline";
import { changeFolderCountText, showUnreadCountText, showTotalCountText, } from "./FolderCountSubmenu.locstring.json";

const ChangeFolderCount = bundleIcon(NumberSymbolFilled, NumberSymbolRegular);
const ChangeFolderCountIcon = <ChangeFolderCount />;

export interface FolderCountSubmenuProps {
    folderId: string;
    distinguishedFolderType?: string;
}

/**
 * Context-menu submenu that lets the user choose whether a folder displays its
 * unread count or its total item count. Shared by the folder tree context menu
 * and the favorites context menu so the two surfaces stay in sync. Callers own
 * the visibility guard (the `fp-foldercount` flight and notes-folder exclusion).
 * The submenu disables itself while offline because changing the display mode
 * persists the folder view state to the server, which is unavailable offline.
 */
function FolderCountSubmenuView(props: FolderCountSubmenuProps) {
    const { folderId, distinguishedFolderType } = props;
    
    const folderCountMode: FolderCountDisplayMode = getFolderCountDisplayMode(folderId) ??
        getDefaultFolderCountDisplayMode(distinguishedFolderType ?? undefined);
    const checkedValues = React.useMemo(() => ({ folderCount: [folderCountMode] }), [folderCountMode]);
    const onCheckedValueChange = React.useCallback<NonNullable<React.ComponentProps<typeof Menu>["onCheckedValueChange"]>>((_event, data) => {
        const next = data.checkedItems[0] as FolderCountDisplayMode | undefined;
        if (next === "unread" || next === "total") {
            lazySetFolderCountDisplayMode.importAndExecute(folderId, next);
        }
    }, [folderId]);
    
    return (<Menu checkedValues={checkedValues} onCheckedValueChange={onCheckedValueChange}>
            <MenuTrigger disableButtonEnhancement={true}>
                <MenuItem icon={ChangeFolderCountIcon} disabled={isOffline()}>
                    {loc(changeFolderCountText)}
                </MenuItem>
            </MenuTrigger>
            <MenuPopover>
                <MenuList>
                    <MenuItemRadio name="folderCount" value="unread">
                        {loc(showUnreadCountText)}
                    </MenuItemRadio>
                    <MenuItemRadio name="folderCount" value="total">
                        {loc(showTotalCountText)}
                    </MenuItemRadio>
                </MenuList>
            </MenuPopover>
        </Menu>);
}

export const FolderCountSubmenu: React.FunctionComponent<FolderCountSubmenuProps> = observer(FolderCountSubmenuView, "FolderCountSubmenu");
